import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputMask from "../../../../u2000Core/components/formControls/input/U2000InputMask";
import type { TFunction } from "i18next";
import { NS } from "../../../U3120";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import { U2000FormStateEnumEditMode } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { useEffect, useMemo, useRef } from "react";
import { doFindCountries, doFindStates, luHandleAppiError } from "../../../U3120Service";
import useU2000AxiosAuthenticated from "../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { u2000ExtractAutoCompleteResults } from "../../../../u2000Core/utils/u2000ExtractAutoCompleteResults";
import { useTranslation } from "react-i18next";
import { isValidId } from "../../../../u2000Core/tools/U2000IdUtils";
import U2000ComboBox from "../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import { CUSTOMER_LANGUAGE_TYPES } from "../../common/customerLanguageTypes";
import { useU3120Context } from "../../../U3120Context";
import type { U3120CustomerType } from "../../../U3120Service";

// Typage des props du composant
interface U3120CustomerComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
}

function U3120CustomerBody<T extends Record<string, any>>({ formStateHandlers, t }: U3120CustomerComponentProps<T>) {
  const { i18n } = useTranslation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { luStateHandlers } = useU3120Context();

  const setStateSelection = useRef<((item: any) => void) | null>(null);
  const setStateInputValue = useRef<((value: string) => void) | null>(null);
  const previousCountryIdRef = useRef<string | null>(null);

  const formData = formStateHandlers.getData() as { customerType?: U3120CustomerType | string | null } | null;
  const currentCustomerTypeValue = formData?.customerType;

  // Options pour le type de client (initLU actifs) + orphelin si type courant inactif / hors catalogue
  const customerTypeOptions = useMemo(() => {
    const personalization = luStateHandlers.getPartialPersonnalization(["customerTypes"]);
    const catalog = (personalization?.customerTypes ?? []) as U3120CustomerType[];
    const types = [...catalog];
    let currentObj: U3120CustomerType | null = null;
    if (currentCustomerTypeValue && typeof currentCustomerTypeValue === "object" && "id" in currentCustomerTypeValue && currentCustomerTypeValue.id) {
      currentObj = currentCustomerTypeValue as U3120CustomerType;
    }
    if (currentObj && !types.some((item) => item.id === currentObj!.id)) {
      types.push(currentObj);
    }
    const isFr = i18n.language === "fr";
    return types.map((item) => {
      const base = isFr
        ? (item.customerTypeNameFr ?? item.customerTypeCode ?? "")
        : (item.customerTypeNameEn ?? item.customerTypeCode ?? "");
      const isOrphan = Boolean(currentObj && item.id === currentObj.id && !catalog.some((c) => c.id === item.id));
      return {
        ...item,
        label: isOrphan ? `${base} (${t("msgCustomerTypeInactiveAssigned", { ns: NS })})` : base,
      };
    });
  }, [luStateHandlers, i18n.language, currentCustomerTypeValue, t]);

  const isCurrentCustomerTypeOrphan = useMemo(() => {
    const personalization = luStateHandlers.getPartialPersonnalization(["customerTypes"]);
    const catalog = (personalization?.customerTypes ?? []) as U3120CustomerType[];
    if (!currentCustomerTypeValue) return false;
    const id =
      typeof currentCustomerTypeValue === "object" && currentCustomerTypeValue !== null && "id" in currentCustomerTypeValue
        ? (currentCustomerTypeValue as U3120CustomerType).id
        : String(currentCustomerTypeValue);
    if (!id) return false;
    return !catalog.some((item) => item.id === id);
  }, [luStateHandlers, currentCustomerTypeValue]);

  // Calcul dynamique des resultFields selon la langue pour les pays
  const countryResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["countryNameFr"];
    if (language === "en") return ["countryNameEn"];
    return ["countryName"]; // fallback pour rétrocompatibilité
  }, [i18n.language]);

  // Calcul dynamique des resultFields selon la langue pour les états
  const stateResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["stateNameFr"];
    if (language === "en") return ["stateNameEn"];
    return ["stateName"]; // fallback pour rétrocompatibilité
  }, [i18n.language]);

  // Format data for U2000ComboBox with multilingual labels
  const languageOptions = useMemo(() => {
    return CUSTOMER_LANGUAGE_TYPES.map((type) => ({
      value: type.value,
      label: typeof type.label === "object" ? (i18n.language === "fr" ? type.label.fr : type.label.en) : String(type.label),
    }));
  }, [i18n.language]);

  // Fonction de recherche de pays
  const onSearchCountry = async (criterias: { fieldName: string; value: any }[]) => {
    try {
      const response = await doFindCountries(u2000AxiosAuthenticated, criterias);
      return u2000ExtractAutoCompleteResults<Record<string, any>>(response.data);
    } catch (error: any) {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return { results: [], totalCount: 0, truncated: false };
    }
  };

  // Fonction d'affichage du pays
  const displayCountry = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.countryNameFr) {
      return item.countryNameFr;
    }
    if (language === "en" && item.countryNameEn) {
      return item.countryNameEn;
    }
    // Fallback sur les anciens champs pour rétrocompatibilité
    return item.countryName || item.name || "";
  };

  const clearStateFields = [
    { fieldName: "state.id", value: null },
    { fieldName: "state.name", value: null },
    { fieldName: "state.stateName", value: null },
    { fieldName: "state.stateNameFr", value: null },
    { fieldName: "state.stateNameEn", value: null },
  ];

  const clearStateUi = () => {
    setStateSelection.current?.(null);
    setStateInputValue.current?.("");
  };

  // Fonction d'extraction des données du pays
  // BL-0394 : clear state seulement si country.id change ; pas de clearStateUi ici
  // (course avec modèle stale — le useEffect pays + sync autocomplete gèrent l’UI).
  const extractCountryData = (result: any) => {
    const previousCountryId = formStateHandlers.getData()?.country?.id;
    const nextCountryId = result?.id ?? null;
    const sameCountry =
      isValidId(previousCountryId) && isValidId(nextCountryId) && String(previousCountryId) === String(nextCountryId);

    if (result) {
      const language = i18n.language;
      let countryName = "";
      if (language === "fr" && result.countryNameFr) {
        countryName = result.countryNameFr;
      } else if (language === "en" && result.countryNameEn) {
        countryName = result.countryNameEn;
      } else {
        countryName = result.countryName || result.name || "";
      }
      const countryFields = [
        { fieldName: "country.id", value: result.id },
        { fieldName: "country.name", value: countryName },
        { fieldName: "country.countryName", value: countryName },
        { fieldName: "country.countryNameFr", value: result.countryNameFr || "" },
        { fieldName: "country.countryNameEn", value: result.countryNameEn || "" },
      ];
      if (sameCountry) {
        return countryFields;
      }
      return [...countryFields, ...clearStateFields];
    }
    return [
      { fieldName: "country.id", value: null },
      { fieldName: "country.name", value: null },
      { fieldName: "country.countryName", value: null },
      { fieldName: "country.countryNameFr", value: null },
      { fieldName: "country.countryNameEn", value: null },
      ...clearStateFields,
    ];
  };

  // Fonction de recherche de province/état
  const onSearchState = async (criterias: { fieldName: string; value: any }[]) => {
    try {
      const data = formStateHandlers.getData();
      if (data && data.country && isValidId(data.country.id)) {
        criterias.push({ fieldName: "countryId", value: data.country.id.toString() });
      }
      const response = await doFindStates(u2000AxiosAuthenticated, criterias);
      return u2000ExtractAutoCompleteResults<Record<string, any>>(response.data);
    } catch (error: any) {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return { results: [], totalCount: 0, truncated: false };
    }
  };

  // Fonction d'affichage de la province/état
  const displayState = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.stateNameFr) {
      return item.stateNameFr;
    }
    if (language === "en" && item.stateNameEn) {
      return item.stateNameEn;
    }
    // Fallback sur les anciens champs pour rétrocompatibilité
    return item.stateName || item.name || "";
  };

  // Fonction d'extraction des données de la province/état
  const extractStateData = (result: any) => {
    if (result) {
      const language = i18n.language;
      let stateName = "";
      if (language === "fr" && result.stateNameFr) {
        stateName = result.stateNameFr;
      } else if (language === "en" && result.stateNameEn) {
        stateName = result.stateNameEn;
      } else {
        // Fallback sur les anciens champs pour rétrocompatibilité
        stateName = result.stateName || result.name || "";
      }
      return [
        { fieldName: "state.id", value: result.id },
        { fieldName: "state.name", value: stateName },
        { fieldName: "state.stateName", value: stateName },
        // Sauvegarder aussi les champs multilingues pour que displayFn puisse fonctionner
        { fieldName: "state.stateNameFr", value: result.stateNameFr || "" },
        { fieldName: "state.stateNameEn", value: result.stateNameEn || "" },
      ];
    } else {
      return [
        { fieldName: "state.id", value: null },
        { fieldName: "state.name", value: null },
        { fieldName: "state.stateName", value: null },
        { fieldName: "state.stateNameFr", value: null },
        { fieldName: "state.stateNameEn", value: null },
      ];
    }
  };

  // Réinitialiser state quand country change ou est effacé
  useEffect(() => {
    const data = formStateHandlers.getData();
    const countryId = data?.country?.id || null;
    const currentCountryId = countryId && isValidId(countryId) ? countryId : null;
    const previousCountryId = previousCountryIdRef.current;

    // Ne réinitialiser que si :
    // 1. Le country a vraiment changé (et ce n'est pas le premier rendu où previousCountryId est null)
    // 2. OU si le country est effacé (currentCountryId est null mais il y avait un state)
    const countryChanged = previousCountryId !== null && previousCountryId !== currentCountryId;
    const countryCleared = previousCountryId !== null && currentCountryId === null;

    if (countryChanged || countryCleared) {
      clearStateUi();
      if (data && data.state && isValidId(data.state.id)) {
        const setNestedValue = (obj: any, path: string, value: any): any => {
          const keys = path.split(".");
          const cloned = JSON.parse(JSON.stringify(obj));
          let current = cloned;
          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] = value;
          return cloned;
        };

        const updatedData = setNestedValue(data, "state.id", null);
        const finalData = setNestedValue(updatedData, "state.name", null);
        const finalData2 = setNestedValue(finalData, "state.stateName", null);
        const finalData3 = setNestedValue(finalData2, "state.stateNameFr", null);
        const finalData4 = setNestedValue(finalData3, "state.stateNameEn", null);
        formStateHandlers.setData(finalData4);
      }
    }

    // Mettre à jour la référence du countryId précédent seulement après le premier rendu
    if (previousCountryId === null || previousCountryId !== currentCountryId) {
      previousCountryIdRef.current = currentCountryId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStateHandlers.getData()?.country?.id]);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Panel Informations générales */}
      <U2000Panel title={t("lblGeneralInfo", { ns: NS })} titleAlign="left" className="w-full">
        <div className="mt-3 flex w-full items-end gap-4">
          <div className="flex-2">
            <U2000InputText
              fieldName="customerCode"
              label={t("lblCustomerCode", { ns: NS })}
              formStateHandlers={formStateHandlers}
              required={true}
              readonly={formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
            />
          </div>
          <div className="flex-3">
            <U2000InputText fieldName="customerName" label={t("lblCustomerName", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
          </div>
          <div className="flex-2">
            <U2000ComboBox
              formStateHandlers={formStateHandlers}
              fieldName="customerType"
              label={t("lblCustomerType", { ns: NS })}
              data={customerTypeOptions}
              keyField="id"
              descField="label"
              getValue={(item) => item.id}
              findItemByValue={(value, data) => {
                if (value == null || value === "") return null;
                const id = typeof value === "object" && value !== null && "id" in value ? (value as U3120CustomerType).id : String(value);
                return data.find((item) => item.id === id) || null;
              }}
              required={true}
            />
            {isCurrentCustomerTypeOrphan && (
              <p className="mt-1 text-sm text-amber-700">{t("msgCustomerTypeInactiveAssigned", { ns: NS })}</p>
            )}
          </div>
          <div className="flex items-center justify-end">
            <div className="flex h-[44px] items-center">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="isRegularCustomer" label={t("lblIsRegularCustomer", { ns: NS })} />
            </div>
          </div>
        </div>
      </U2000Panel>

      {/* Ligne Adresse et Coordonnées côte à côte */}
      <div className="flex items-stretch gap-4">
        {/* Colonne gauche - Panel Adresse */}
        <div className="flex flex-[66_66_0%] flex-col gap-4">
          <U2000Panel title={t("lblAddress", { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000InputText fieldName="address" label={t("lblAddress", { ns: NS })} formStateHandlers={formStateHandlers} />
              </div>
            </div>

            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000InputText fieldName="city" label={t("lblCity", { ns: NS })} formStateHandlers={formStateHandlers} />
              </div>
              <div className="flex-1">
                <U2000InputMask fieldName="postalCode" label={t("lblPostalCode", { ns: NS })} mask="postalCodeCA" formStateHandlers={formStateHandlers} />
              </div>
            </div>

            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName="country"
                  mainFieldName="countryName"
                  label={t("lblCountry", { ns: NS })}
                  onSearch={onSearchCountry}
                  displayFn={displayCountry}
                  overrideExtractDataFn={extractCountryData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={countryResultFields}
                  required={true}
                />
              </div>
              <div className="flex-1">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName="state"
                  mainFieldName="stateName"
                  label={t("lblState", { ns: NS })}
                  onSearch={onSearchState}
                  displayFn={displayState}
                  overrideExtractDataFn={extractStateData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={stateResultFields}
                  readonly={!formStateHandlers.getData()?.country?.id || formStateHandlers.getData()?.country?.id === ""}
                  required={true}
                  onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
                    setStateSelection.current = setCurrentSelection;
                    setStateInputValue.current = setInputValue;
                  }}
                />
              </div>
              <div className="flex flex-col">
                <span aria-hidden="true" className="block text-sm font-medium mb-1.5 select-none invisible">
                  &nbsp;
                </span>
                <div className="flex h-[44px] items-center">
                  <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="taxable" label={t("lblTaxable", { ns: NS })} />
                </div>
              </div>
            </div>
          </U2000Panel>
        </div>

        {/* Colonne droite - Panel Coordonnées */}
        <div className="flex flex-[34_34_0%] flex-col gap-4">
          <U2000Panel title={t("lblContactInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col">
            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000InputMask fieldName="officePhone1" label={t("lblOfficePhonePrincipal1", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} />
              </div>
            </div>

            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000InputMask fieldName="officePhone2" label={t("lblOfficePhonePrincipal2", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} />
              </div>
            </div>

            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000ComboBox
                  formStateHandlers={formStateHandlers}
                  fieldName="language"
                  label={t("lblLanguage", { ns: NS })}
                  data={languageOptions}
                  keyField="value"
                  descField="label"
                  getValue={(item) => item.value}
                  findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
                />
              </div>
            </div>
          </U2000Panel>
        </div>
      </div>
    </div>
  );
}

export default U3120CustomerBody;
